/**
 * SRAC Membership Info Access Levels
 *
 * Decides which `membershipInfo` access levels a given viewer may ask Firestore
 * for, and builds the filtered query that asks for exactly those. Every client
 * that lists `membershipInfo` goes through here.
 *
 * This exists because Firestore does not filter query results by security
 * rules: a list either returns everything it matched or is rejected outright.
 * The collection previously carried `allow list: if true`, on the belief that
 * per-document read rules would hide restricted fields — they do not, and the
 * club's gate lock number (`accessLevel: 'members'`) and officer distribution
 * address (`accessLevel: 'officers'`) were readable by anyone with no login.
 *
 * With that removed, the query itself must be narrowed to what the caller can
 * read. Asking for too much now fails the whole query and blanks the club
 * information on the page; asking for too little silently hides fields the
 * member is entitled to. Both mistakes are quiet, so the decision lives in one
 * tested place rather than at each call site.
 *
 * Copyright — see the LICENSE file in the repository root for terms.
 */

import {
  collection,
  query,
  where,
  type DocumentData,
  type Firestore,
  type Query,
} from 'firebase/firestore';

/** The access tiers a `membershipInfo` field may be marked with. */
export type MembershipInfoAccessLevel = 'public' | 'users' | 'members' | 'officers';

/** What the current viewer is, as far as club information is concerned. */
export interface MembershipInfoViewer {
  /** Whether anyone is signed in at all. */
  signedIn: boolean;
  /** Whether the signed-in user has a linked or active membership. */
  hasMembership: boolean;
  /** Whether the signed-in user administers the club's information. */
  isOfficerOrAdmin: boolean;
}

/**
 * Resolve the access levels a viewer may request.
 *
 * Every tier requires a signed-in user except `public`, mirroring the Firestore
 * rule exactly — a stale membership flag on a signed-out client must not widen
 * the query, or the rules reject it and the page loses even its public fields.
 *
 * @param viewer - Who is asking.
 * @returns The access levels safe to include in a query, widest last.
 */
export function membershipInfoAccessLevels(
  viewer: MembershipInfoViewer,
): MembershipInfoAccessLevel[] {
  const levels: MembershipInfoAccessLevel[] = ['public'];
  if (!viewer.signedIn) return levels;

  levels.push('users');
  if (viewer.hasMembership || viewer.isOfficerOrAdmin) levels.push('members');
  if (viewer.isOfficerOrAdmin) levels.push('officers');
  return levels;
}

/**
 * Build the `membershipInfo` query a viewer is allowed to run.
 *
 * @param firestore - The Firestore instance.
 * @param viewer - Who is asking.
 * @returns A query filtered to the caller's readable access levels. Typed as
 *   `Query<DocumentData>` so callers keep `doc.data()` typed as document data
 *   rather than `unknown`.
 */
export function membershipInfoQuery(
  firestore: Firestore,
  viewer: MembershipInfoViewer,
): Query<DocumentData> {
  return query(
    collection(firestore, 'membershipInfo'),
    where('accessLevel', 'in', membershipInfoAccessLevels(viewer)),
  );
}
